import {
  type AttendanceDayView,
  type AttendanceSchedule,
  dayLabel,
  duration,
  shortTime,
} from '../api/attendance';
import { Icon } from './Icon';
import { Modal } from './Overlay';

const DAY_NAMES = ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'];

const minutesOf = (time: string) => {
  const [h = 0, m = 0] = time.split(':').map(Number);
  return h * 60 + m;
};

/**
 * Детализация дня: первый проход — вход, дальше по очереди выход и вход.
 * Показывает время в офисе, выходы и время вне офиса, опоздание и всю ленту проходов.
 */
export function AttendanceDayDetail({
  day,
  schedule,
  person,
  onClose,
}: {
  day: AttendanceDayView;
  schedule: AttendanceSchedule | null;
  person?: string;
  onClose: () => void;
}) {
  const weekday = DAY_NAMES[new Date(`${day.date}T00:00:00Z`).getUTCDay()] ?? '';
  const title = `${weekday}, ${dayLabel(day.date).slice(0, 5)}${person ? ` · ${person}` : ''}`;

  // Шкала дня: от первого до последнего прохода, отрезки в офисе и вне офиса.
  const start = minutesOf(day.readings[0] ?? day.latestAt);
  const end = Math.max(minutesOf(day.latestAt), start + 1);
  const span = end - start;
  const segments = day.passes.slice(0, -1).map((pass, index) => {
    const next = day.passes[index + 1];
    const from = minutesOf(pass.time);
    const to = next ? minutesOf(next.time) : from;
    return {
      key: `${pass.time}-${index}`,
      inside: pass.kind === 'in',
      left: ((from - start) / span) * 100,
      width: Math.max(((to - from) / span) * 100, 0.4),
      label: `${pass.kind === 'in' ? 'В офисе' : 'Вне офиса'} ${shortTime(pass.time)}–${next ? shortTime(next.time) : ''}`,
    };
  });
  const longest = day.breaks.reduce((max, item) => Math.max(max, item.minutes), 0);
  // Ключи проходов: время может повториться, поэтому нумеруем одинаковые отметки.
  const seen = new Map<string, number>();
  const steps = day.passes.map((pass, index) => {
    const seq = (seen.get(pass.time) ?? 0) + 1;
    seen.set(pass.time, seq);
    return { ...pass, index, key: `${pass.time}#${seq}` };
  });

  return (
    <Modal title={title} onClose={onClose}>
      <div className="day-detail">
        <div className="day-chips">
          {day.weekend ? <span className="status status-neutral">Выходной</span> : null}
          {day.open ? <span className="status status-info">День ещё идёт</span> : null}
          {day.lateMinutes != null ? (
            <span className="status status-bad">Опоздание {duration(day.lateMinutes)}</span>
          ) : schedule?.lateAfter && !day.weekend ? (
            <span className="status status-ok">Вовремя</span>
          ) : null}
          {day.assumedArrival ? <span className="status status-bad">Приход не отмечен</span> : null}
          {day.assumedDeparture ? <span className="status status-bad">Уход не отмечен</span> : null}
          {day.missed ? (
            <span className="status status-bad">Пропущено отметок: {day.missed}</span>
          ) : day.unpaired && !day.open ? (
            <span className="status status-bad">Нет отметки ухода</span>
          ) : null}
        </div>

        <dl className="day-facts">
          <div>
            <dt>Вход</dt>
            <dd>
              {shortTime(day.firstAt)}
              {day.assumedArrival ? <small className="day-note">по первой отметке дня</small> : null}
            </dd>
          </div>
          <div>
            <dt>{day.open ? 'Последний проход' : 'Уход'}</dt>
            <dd>{day.open ? shortTime(day.latestAt) : day.lastAt ? shortTime(day.lastAt) : 'нет отметки'}</dd>
          </div>
          <div>
            <dt>В офисе</dt>
            <dd>{duration(day.insideMinutes)}</dd>
          </div>
          <div>
            <dt>Вне офиса</dt>
            <dd>
              {duration(day.outsideMinutes)}
              <small className="day-note">
                {day.breaks.length
                  ? `${day.breaks.length} ${day.breaks.length === 1 ? 'выход' : day.breaks.length < 5 ? 'выхода' : 'выходов'}`
                  : 'без выходов'}
                {longest ? ` · самый долгий ${duration(longest)}` : ''}
              </small>
            </dd>
          </div>
          {schedule?.lateAfter ? (
            <div>
              <dt>График</dt>
              <dd>
                с {shortTime(schedule.workStart)}
                <small className="day-note">опоздание после {shortTime(schedule.lateAfter)}</small>
              </dd>
            </div>
          ) : null}
        </dl>

        {day.passes.length > 1 ? (
          <div className="day-scale" aria-hidden="true">
            <div className="day-scale-track">
              {segments.map((segment) => (
                <span
                  key={segment.key}
                  className={segment.inside ? 'in' : 'out'}
                  style={{ left: `${segment.left}%`, width: `${segment.width}%` }}
                  title={segment.label}
                />
              ))}
            </div>
            <div className="day-scale-labels">
              <span>{shortTime(day.readings[0] ?? null)}</span>
              <span>{shortTime(day.latestAt)}</span>
            </div>
          </div>
        ) : null}

        <ol className="day-timeline">
          {steps.map((pass) => {
            const next = steps[pass.index + 1];
            const gap = next ? minutesOf(next.time) - minutesOf(pass.time) : null;
            const last = pass.index === steps.length - 1;
            return (
              <li key={pass.key} className={pass.kind}>
                <span className="day-time">{shortTime(pass.time)}</span>
                <span className="day-kind">
                  <Icon name={pass.kind === 'in' ? 'check' : 'external'} size={13} />
                  {pass.kind === 'in' ? (pass.index === 0 ? 'Вход' : 'Вернулся') : last ? 'Уход' : 'Выход'}
                </span>
                {gap !== null ? (
                  <span className="day-gap">
                    {pass.kind === 'in' ? 'в офисе' : 'вне офиса'} {duration(gap)}
                  </span>
                ) : pass.kind === 'in' && !day.open && last ? (
                  <span className="day-gap text-bad">нет отметки выхода</span>
                ) : null}
              </li>
            );
          })}
        </ol>

        {day.assumedArrival || day.assumedDeparture ? (
          <p className="notice">
            <Icon name="alert" size={14} />
            {day.assumedArrival && day.assumedDeparture
              ? 'Турникет не отметил ни приход, ни уход: за них взяты первая и последняя отметки дня.'
              : day.assumedArrival
                ? 'Турникет не отметил приход: за приход взята первая отметка дня, опоздание считается от неё.'
                : 'Турникет не отметил уход: за уход взята последняя отметка дня.'}{' '}
            Время в офисе за такой день приблизительное.
          </p>
        ) : day.missed ? (
          <p className="notice">
            <Icon name="alert" size={14} />
            Две отметки подряд в одну сторону: {day.missed} раз система проходов не зафиксировала обратный
            проход (например, человек прошёл вслед за коллегой). Время в офисе за этот день приблизительное.
          </p>
        ) : day.unpaired && !day.open ? (
          <p className="notice">
            <Icon name="alert" size={14} />
            Проходов нечётное число: одна отметка, видимо, пропущена (например, прошли вслед за другим). Время
            в офисе за этот день приблизительное.
          </p>
        ) : null}
        {day.truncated ? (
          <p className="notice">
            <Icon name="alert" size={14} />
            Система проходов выгрузила {day.readings.length} из {day.readsCount} отметок: разбор на входы и
            выходы приблизительный.
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
